/**
 * MetaInfoSettingsTab — global Settings → Meta Info view. Lists every
 * persisted MetaInfo record (loose foreign keys + JSON column declarations)
 * across all connections and databases, grouped first by connection then by
 * database. Connections collapse by default when there are more than three;
 * databases inside a connection collapse by default when that connection
 * has more than three databases with meta-info. Removes flow through the
 * same IPC + invalidation path as the per-database manager dialog, with
 * confirmation rendered via the app-level `<GlobalConfirmDialog />` so the
 * AlertDialog is never stacked inside this Settings Dialog.
 */

import { useMemo } from 'react';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { useAllMetaInfo } from '../../../workspace/common/hooks/useMetaInfo';
import { MetaInfoConnectionGroup } from './meta-info/MetaInfoConnectionGroup';
import { groupMetaInfoByConnection } from './meta-info/groupMetaInfoByConnection';
import { useMetaInfoCollapseState } from './meta-info/useMetaInfoCollapseState';
import { useMetaInfoRemoveConfirm } from './meta-info/useMetaInfoRemoveConfirm';

export function MetaInfoSettingsTab() {
  const { data, isLoading } = useAllMetaInfo();
  const connections = useConnectionStore((s) => s.connections);

  const groups = useMemo(
    () => groupMetaInfoByConnection(data ?? [], connections),
    [data, connections],
  );

  const collapse = useMetaInfoCollapseState(groups.length);
  const confirm = useMetaInfoRemoveConfirm();

  if (isLoading) {
    return (
      <div className="text-sm text-muted-foreground">Loading meta info…</div>
    );
  }

  if (groups.length === 0) {
    return (
      <div
        className="text-sm text-muted-foreground"
        data-testid="metainfo-settings-empty"
      >
        No meta info recorded yet. Loose foreign keys, JSON-column, and
        enum-column declarations made on any database will appear here.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="metainfo-settings-tab">
      <p className="text-xs text-muted-foreground">
        User-declared metadata that lives outside the schema, grouped by
        connection and database. Removing an entry won't change data — it
        only deletes the declaration.
      </p>
      {groups.map((group) => (
        <MetaInfoConnectionGroup
          key={group.connectionId}
          connectionId={group.connectionId}
          displayName={group.displayName}
          databases={group.databases}
          open={collapse.isConnectionOpen(group.connectionId)}
          onToggle={() => collapse.toggleConnection(group.connectionId)}
          isDatabaseOpen={(database) =>
            collapse.isDatabaseOpen(
              group.connectionId,
              database,
              group.databases.length,
            )
          }
          onToggleDatabase={(database) =>
            collapse.toggleDatabase(
              group.connectionId,
              database,
              group.databases.length,
            )
          }
          onRemoveLooseRel={(database, rel, label) =>
            confirm.requestRemoveLoose(group.connectionId, database, rel, label)
          }
          onRemoveJsonCol={(database, decl) =>
            confirm.requestRemoveJson(group.connectionId, database, decl)
          }
          onRemoveEnumCol={(database, decl) =>
            confirm.requestRemoveEnum(group.connectionId, database, decl)
          }
          onRemoveSpatialCol={(database, decl) =>
            confirm.requestRemoveSpatial(group.connectionId, database, decl)
          }
        />
      ))}
    </div>
  );
}
